import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../../lib/cn';

export const Sidebar = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <aside
      ref={ref}
      className={cn(
        'hidden w-60 shrink-0 flex-col border-r bg-background md:flex',
        className,
      )}
      {...props}
    />
  ),
);
Sidebar.displayName = 'Sidebar';

export const SidebarHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex h-14 items-center gap-2 border-b px-4 text-sm font-semibold', className)}
      {...props}
    />
  ),
);
SidebarHeader.displayName = 'SidebarHeader';

export const SidebarNav = React.forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => (
    <nav ref={ref} className={cn('flex flex-1 flex-col gap-1 overflow-y-auto p-2', className)} {...props} />
  ),
);
SidebarNav.displayName = 'SidebarNav';

export interface SidebarNavItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  active?: boolean;
  icon?: React.ReactNode;
  /** 'md' (20px) for top-level rows, 'sm' (16px) for nested ones, so
   *  levels stay distinct. */
  iconSize?: 'sm' | 'md';
}

export const SidebarNavItem = React.forwardRef<HTMLButtonElement, SidebarNavItemProps>(
  ({ className, asChild = false, active = false, icon, iconSize = 'md', children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref as React.Ref<HTMLButtonElement>}
        className={cn(
          'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'text-muted-foreground hover:bg-accent hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          active && 'bg-accent text-foreground',
          className,
        )}
        {...props}
      >
        {/* The selected row's icon takes the accent colour, and top-level
            icons are 20px (operator review, 23 Oct: 16px read as small;
            bigger, not a heavier line). Icons draw in currentColor and
            are sized here, so callers pass them as they are. */}
        {icon && (
          <span
            className={cn(
              'flex shrink-0 items-center justify-center transition-colors',
              iconSize === 'md'
                ? 'h-5 w-5 [&>svg]:h-5 [&>svg]:w-5'
                : 'h-4 w-4 [&>svg]:h-4 [&>svg]:w-4',
              active && 'text-primary',
            )}
          >
            {icon}
          </span>
        )}
        <span className="truncate">{children}</span>
      </Comp>
    );
  },
);
SidebarNavItem.displayName = 'SidebarNavItem';

export const SidebarFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('mt-auto border-t p-2', className)} {...props} />
  ),
);
SidebarFooter.displayName = 'SidebarFooter';
